/**
 * MainLayout Component - Unified Layout Wrapper
 * IMPORTANT: Uses only GlassHeader (TopBar is integrated into GlassHeader)
 * Global SEO infrastructure components are mounted here to propagate to ALL pages.
 * 
 * SEO signals consolidated into GlobalSEOSignals (single Helmet) for CWV optimization.
 */
import { ReactNode, lazy, Suspense, useMemo } from 'react';
import { WebVitalsReporter } from '@/components/seo/WebVitalsReporter';
import { useLocation } from 'react-router-dom';
import { GlassHeader } from '@/components/layout/GlassHeader';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { WhatsAppWidget } from '@/components/widgets/WhatsAppWidget';
import { useLanguage } from '@/contexts/LanguageContextNew';

import { Breadcrumbs } from '@/components/widgets/Breadcrumbs';
import { ExitIntentPopup } from '@/components/widgets/ExitIntentPopup';
import { TrustBar } from '@/components/widgets/TrustBar';
import { SchemaMarkup } from '@/components/SEO/SchemaMarkup';
// SocialProofNotification removed — intrusive
import { StickyCTABar } from '@/components/cro/StickyCTABar';
import { PerformanceConsolidator } from '@/components/seo/PerformanceConsolidator';
import { DiscoveryCallCTA } from '@/components/conversion/DiscoveryCallCTA';
import { FloatingQuoteButton } from '@/components/conversion/FloatingQuoteButton';
import { PerformanceResourceHints } from '@/components/seo/PerformanceResourceHints';
import { GlobalImageDefaults } from '@/components/perf/GlobalImageDefaults';
import { GlobalA11yEnhancer } from '@/components/perf/GlobalA11yEnhancer';
import { IdleMount } from '@/components/perf/IdleMount';
import { RenderErrorBoundary } from '@/components/layout/RenderErrorBoundary';

import { MultilingualKeywordNoscript } from '@/components/seo/MultilingualKeywordNoscript';

// Single consolidated SEO signals component (replaces 5 separate Helmet instances)
import { GlobalSEOSignals } from '@/components/seo/GlobalSEOSignals';
import { EnterpriseSchemas } from '@/components/seo/EnterpriseSchemas';
import { DublinCoreMeta } from '@/components/seo/DublinCoreMeta';
import { BreadcrumbAutoGenerator } from '@/components/seo/BreadcrumbAutoGenerator';
import { SchemaPruner } from '@/components/seo/SchemaPruner';

// Lazy-load heavy SEO components
const LongTailKeywordInjector = lazy(() => import('@/components/seo/LongTailKeywordInjector').then(m => ({ default: m.LongTailKeywordInjector })));
const GlobalSearchEngineOptimizer = lazy(() => import('@/components/seo/GlobalSearchEngineOptimizer').then(m => ({ default: m.GlobalSearchEngineOptimizer })));
const AdvancedCrawlDirectives = lazy(() => import('@/components/seo/AdvancedCrawlDirectives').then(m => ({ default: m.AdvancedCrawlDirectives })));
const YandexBaiduOptimizer = lazy(() => import('@/components/seo/YandexBaiduOptimizer').then(m => ({ default: m.YandexBaiduOptimizer })));
const SpeculationRules = lazy(() => import('@/components/seo/SpeculationRules').then(m => ({ default: m.SpeculationRules })));
const CrawlabilityEnhancer = lazy(() => import('@/components/seo/CrawlabilityEnhancer').then(m => ({ default: m.CrawlabilityEnhancer })));

interface MainLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  darkMode?: boolean;
  /** SOFT404-2410: forward noindex/noCanonical to the layout-level SEOHead so
   *  error pages (404) don't get canonical/hreflang re-added by the layout. */
  noIndex?: boolean;
  noCanonical?: boolean;
}

export function MainLayout({ 
  children, 
  title, 
  description,
  darkMode = true,
  noIndex = false,
  noCanonical = false,
}: MainLayoutProps) {
  const { language } = useLanguage();
  const location = useLocation();
  const pagePath = location.pathname;
  // QUICK-SUMMARY-1810c: lighter floating chrome on the quote widget
  const isQuoteWidget = /\/start(\/|$)/.test(pagePath);

  const pageType = useMemo(() => {
    const path = pagePath.replace(/^\/[a-z]{2}\/?/, '/');
    if (path === '/' || path === '') return 'home' as const;
    if (/^\/(contact)/.test(path)) return 'contact' as const;
    if (/^\/(about|leadership|fact-sheet)/.test(path)) return 'about' as const;
    if (/^\/(faq)/.test(path)) return 'faq' as const;
    if (/^\/(pricing)/.test(path)) return 'pricing' as const;
    if (/^\/(compare)/.test(path)) return 'compare' as const;
    if (/^\/(blog|insights|newsroom)/.test(path)) return 'blog' as const;
    if (/^\/(industries)/.test(path)) return 'industry' as const;
    if (/^\/(locations|regions)/.test(path)) return 'location' as const;
    if (/^\/(services|bpo|call-center|it-development|web-development|digital-marketing|seo-audit)/.test(path)) return 'service' as const;
    return 'home' as const;
  }, [pagePath]);

  return (
    <>
      <SEOHead title={title} description={description} noIndex={noIndex} noCanonical={noCanonical} />
      <SchemaMarkup
        pageType={pageType}
        lang={language}
        pagePath={pagePath}
        pageTitle={title || 'BPO Contact'}
        pageDescription={description || 'Enterprise BPO & Digital Factory'}
      />
      <PerformanceConsolidator />
      <WebVitalsReporter />
      <GlobalSEOSignals />
      <EnterpriseSchemas />
      <DublinCoreMeta />
      <BreadcrumbAutoGenerator />
      <SchemaPruner />
      {/* Heavy SEO components — deferred behind requestIdleCallback so they
          don't bloat FCP/LCP or initial DOM. Mounted after the browser is idle. */}
      <IdleMount>
        <Suspense fallback={null}>
          <LongTailKeywordInjector />
          <GlobalSearchEngineOptimizer />
          <AdvancedCrawlDirectives />
          <YandexBaiduOptimizer />
          <SpeculationRules />
          <CrawlabilityEnhancer />
        </Suspense>
      </IdleMount>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-cyan focus:text-navy focus:rounded-lg focus:font-semibold focus:shadow-lg"
      >
        Skip to content
      </a>
      <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-muted/20' : 'bg-background'}`}>
        <GlassHeader />
        <main id="main-content" role="main" aria-label="Page content" className="flex-1 pt-16 md:pt-20">
          <Breadcrumbs />
          <RenderErrorBoundary name="page">{children}</RenderErrorBoundary>
          <DiscoveryCallCTA />
        </main>
        <Footer />
        {/* Spacer for fixed TrustBar + StickyCTABar */}
        <div className="h-14 md:h-16" />
        <WhatsAppWidget />

        {!isQuoteWidget && <TrustBar language={language} />}
        <ExitIntentPopup />
        {/* SocialProofNotification removed — too intrusive */}
        <StickyCTABar />
        <FloatingQuoteButton />
        <PerformanceResourceHints />
        <GlobalImageDefaults />
        <GlobalA11yEnhancer />
        
        <MultilingualKeywordNoscript />
      </div>
      
    </>
  );
}
